export interface IGraphNode<T> {
	link: (node: GraphNode<T>, weight: number, backlink?: boolean) => void;
	unlink: (node: GraphNode<T>, backlink?: boolean) => void;

	linksTo: (node: GraphNode<T>) => boolean;
}

export type TLinkType = 'normal' | 'ramp' | 'stair' | 'elevator' | 'escalator' | 'travelator' | 'virtual';

export interface TCoords {
	x: number;
	y: number;
	z?: number;
}

export class GraphNode<T> implements IGraphNode<T> {
	id: symbol;
	rawId: string;
	value: T | null;
	coords: TCoords;
	weights: Record<symbol, number> = {};
	distances: Record<symbol, number> = {};
	links: Array<GraphNode<T>> = [];
	linkTypes: Record<symbol, TLinkType> = {};
	type: string;
	companyId?: number;

	constructor(id: string | number, value: T | null, coords: TCoords, type: string = 'normal') {
		this.id = Symbol(id.toString());
		this.rawId = id.toString();
		this.value = value;
		this.coords = coords;
		this.type = type;
	}

	link(
		node: GraphNode<T>,
		weight = 1,
		backlink = true,
		backlinkWeight: number | null | undefined = null,
		dist: number | null = null
	): void {
		if (!this.links.includes(node)) {
			this.links.push(node);

			let distance = dist ?? 1;

			if (dist === null) {
				distance = Math.hypot(
					this.coords.x - node.coords.x,
					this.coords.y - node.coords.y,
					(this.coords.z ?? 0) - (node.coords.z ?? 0)
				);
			}

			this.weights[node.id] = weight;
			this.distances[node.id] = distance;

			if (backlink) {
				node.links.push(this);
				node.weights[this.id] = backlinkWeight ?? weight;
				node.distances[this.id] = distance;
			}
		}
	}

	unlink(node: GraphNode<T>, backlink = true): void {
		if (this.links.includes(node)) {
			this.links = this.links.filter((n) => n !== node);
			// eslint-disable-next-line @typescript-eslint/no-dynamic-delete
			delete this.weights[node.id];
			// eslint-disable-next-line @typescript-eslint/no-dynamic-delete
			delete this.distances[node.id];

			if (backlink) {
				node.links = node.links.filter((n) => n !== this);
				// eslint-disable-next-line @typescript-eslint/no-dynamic-delete
				delete node.weights[this.id];
				// eslint-disable-next-line @typescript-eslint/no-dynamic-delete
				delete node.distances[this.id];
			}
		}
	}

	linksTo(node: GraphNode<T>): boolean {
		return this.links.includes(node);
	}
}

export default GraphNode;
